<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import PlaygroundEndpointPicker from './components/PlaygroundEndpointPicker.vue';
import PlaygroundFieldForm from './components/PlaygroundFieldForm.vue';
import PlaygroundPreview from './components/PlaygroundPreview.vue';
import painelAPI from 'dashboard/api/painel';

const store = useStore();
const { t } = useI18n();
const endpointId = ref('create');
const payload = ref({});
const pathParams = ref({});
const queryParams = ref({});
const responseData = ref(null);
const errorData = ref(null);

const fieldDefinitions = computed(
  () => store.getters['painelFieldDefinitions/getFieldDefinitions'] || []
);
const accessToken = computed(() => store.getters.getCurrentUser?.access_token);

onMounted(() => {
  store.dispatch('painelFieldDefinitions/fetch');
});

const execute = async () => {
  errorData.value = null;
  responseData.value = null;
  try {
    let res;
    if (endpointId.value === 'create') {
      res = await painelAPI.createAppointment(payload.value);
    } else if (endpointId.value === 'update') {
      res = await painelAPI.updateAppointment(
        pathParams.value.id,
        payload.value
      );
    } else if (endpointId.value === 'destroy') {
      res = await painelAPI.deleteAppointment(
        pathParams.value.id,
        payload.value
      );
    } else if (endpointId.value === 'list_upcoming') {
      res = await painelAPI.fetchUpcomingAppointments();
    } else if (endpointId.value === 'confirm') {
      res = await painelAPI.confirmAppointment(
        pathParams.value.id,
        payload.value
      );
    } else if (endpointId.value === 'create_human_attention') {
      res = await painelAPI.createHumanAttentionRequest(payload.value);
    } else if (endpointId.value === 'mark_read_by_phone') {
      res = await painelAPI.markReadByPhone(payload.value);
    } else {
      res = await painelAPI.fetchAppointments(queryParams.value);
    }
    responseData.value = res.data;
  } catch (e) {
    errorData.value = e.response?.data || { message: e.message };
  }
};

const downloadSpec = async format => {
  const res = await painelAPI.fetchApiSpec(format);
  const blob = new Blob([res.data], {
    type: format === 'json' ? 'application/json' : 'text/yaml',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `painel-api.${format}`;
  a.click();
  URL.revokeObjectURL(url);
};
</script>

<template>
  <div
    class="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)_minmax(0,1fr)]"
  >
    <PlaygroundEndpointPicker v-model="endpointId" />

    <PlaygroundFieldForm
      :endpoint-id="endpointId"
      :field-definitions="fieldDefinitions"
      @update:payload="payload = $event"
      @update:path-params="pathParams = $event"
      @update:query-params="queryParams = $event"
    />

    <div class="flex flex-col gap-4">
      <PlaygroundPreview
        :endpoint-id="endpointId"
        :path-params="pathParams"
        :query-params="queryParams"
        :payload="payload"
        :access-token="accessToken"
      />
      <button
        class="rounded bg-woot-500 px-4 py-2 text-white hover:bg-woot-600"
        @click="execute"
      >
        {{ t('PAINEL.API_PLAYGROUND.EXECUTE') }}
      </button>
      <div v-if="responseData" class="overflow-x-auto">
        <pre class="rounded bg-n-slate-2 p-2 text-xs">{{
          JSON.stringify(responseData, null, 2)
        }}</pre>
      </div>
      <div v-if="errorData" class="overflow-x-auto">
        <pre class="rounded bg-ruby-50 p-2 text-xs">{{
          JSON.stringify(errorData, null, 2)
        }}</pre>
      </div>
    </div>
  </div>

  <div class="mt-8 border-t border-n-slate-5 pt-6">
    <h4 class="mb-2 text-sm font-semibold text-n-slate-12">
      {{ t('PAINEL.API_PLAYGROUND.DOWNLOAD_TITLE') }}
    </h4>
    <div class="flex gap-2">
      <button
        class="rounded border border-n-slate-5 px-3 py-1.5 text-sm hover:bg-n-slate-2"
        @click="downloadSpec('yaml')"
      >
        {{ t('PAINEL.API_PLAYGROUND.FORMAT_YAML') }}
      </button>
      <button
        class="rounded border border-n-slate-5 px-3 py-1.5 text-sm hover:bg-n-slate-2"
        @click="downloadSpec('json')"
      >
        {{ t('PAINEL.API_PLAYGROUND.FORMAT_JSON') }}
      </button>
    </div>
  </div>
</template>
